<template>
  <div
    v-if="flashcard && flashcard.id"
    :class="{'prevent-select': shouldBlurQuestion || shouldBlurAnswers}"
    class="flashcard-component-container"
  >
    <div
      class="question-stem-container"
      :class="{ 'hide': !shouldShowStemContainer }"
    >
      <div
        class="question-stem-content"
        data-test="question-stem"
      >
        <scenario-box
          v-if="scenarioBoxTopic"
          :flashcard="flashcard"
          :topic="scenarioBoxTopic"
        ></scenario-box>
        <question-stem
          v-if="!terminology && !isOneColumnType"
          :class="{ blur: shouldBlurQuestion }"
          :flashcard="flashcard"
        />
        <asset-tray
          class="asset-tray"
          :flashcard="flashcard"
        ></asset-tray>
      </div>
    </div>
    <div
      class="answer-component-container"
      data-test="answer-component"
      :class="{ 'only-one-column': !shouldShowStemContainer }"
    >
      <template v-if="!shouldShowStemContainer">
        <scenario-box
          v-if="scenarioBoxTopic"
          :flashcard="flashcard"
          :topic="scenarioBoxTopic"
        ></scenario-box>
        <question-stem
          v-if="!terminology"
          :class="{ blur: shouldBlurQuestion, 'hide': isOneColumnType }"
          :flashcard="flashcard"
        />
        <asset-tray
          class="asset-tray"
          :class="{ 'hide-asset-tray': !showAssetTray }"
          :flashcard="flashcard"
        ></asset-tray>
      </template>
      <component
        :is="flashcardType"
        :class="{ blur: shouldBlurAnswers }"
        :answered="false"
        :answer-ids="answerIds"
        :disabled="disabled"
        :flashcard="flashcard"
        :flashcard-state="flashcardState"
        :raw-answer-data="rawAnswerData"
        @set-answer-ids="$emit('set-answer-ids', $event)"
        @set-raw-answer-data="$emit('set-raw-answer-data', $event)"
        @try-answer-flashcard="$emit('try-answer-flashcard')"
      />
      <mobile-asset-tray-activator
        class="one-column"
        :flashcard="flashcard"
      >
      </mobile-asset-tray-activator>
    </div>
  </div>
</template>

<script>
import { defineAsyncComponent, toRef } from 'vue';
import MobileAssetTrayActivator from '@components/flashcards-v2/fragments/MobileAssetTrayActivator.vue';
import AssetTray from '@components/flashcards-v2/fragments/AssetTray';
import AssetTrayMixin from '@/mixins/asset-tray';
import { useScenarioBox } from '@/composables/useScenarioBox';

// Question Types
import FractionalNumericEntry from '@components/flashcards-v2/types/FractionalNumericEntry';
import MultipleChoice from '@components/flashcards-v2/types/MultipleChoice';
import NumericEntry from '@components/flashcards-v2/types/NumericEntry';
import ScenarioBox from '@components/flashcards-v2/fragments/ScenarioBox';
import Terminology from '@components/flashcards/types/terminology';
import { asyncImport, rateLimitingEnabled } from '@/utils';
import Rating from '@components/singletons/rating';
import RatingClass from '@/classes/rating';

const BlankTextCompletion = defineAsyncComponent(() => asyncImport(import('@components/flashcards-v2/types/BlankTextCompletion')));
const Bowtie = defineAsyncComponent(() => asyncImport(import('@components/flashcards-v2/types/BowTie')));
const ClozeDropdown = defineAsyncComponent(() => asyncImport(import('@components/flashcards-v2/types/ClozeDropdown')));
const DragAndDrop = defineAsyncComponent(() => asyncImport(import('@components/flashcards-v2/types/DragAndDrop')));
const Highlight = defineAsyncComponent(() => asyncImport(import('@components/flashcards-v2/types/Highlight')));
const Hotspot = defineAsyncComponent(() => asyncImport(import('@components/flashcards-v2/types/HotSpot')));
const MatrixGrid = defineAsyncComponent(() => asyncImport(import('@components/flashcards-v2/types/MatrixGrid')));
const OrderedResponse = defineAsyncComponent(() => asyncImport(import('@components/flashcards-v2/types/OrderedResponse')));

// Other Components
import AssetAssociationsList from '@components/assetAssociations/list';
import Discussions from '@components/discussions/discussions';
import ExamTip from '@components/flashcards/exam-tip';
import QuestionStem from '@components/flashcards-v2/fragments/QuestionStem';
import Rationale from '@components/flashcards/rationale';

import MathMlMixin from '@/mixins/mathml';

import { flashcardType } from '@/utils/flashcards';

export default {
  components: {
    AssetTray,
    MobileAssetTrayActivator,
    // Question Types
    BlankTextCompletion,
    Bowtie,
    ClozeDropdown,
    DragAndDrop,
    FractionalNumericEntry,
    Highlight,
    Hotspot,
    MatrixGrid,
    MultipleChoice,
    NumericEntry,
    OrderedResponse,
    Rating,
    ScenarioBox,
    Terminology,
    // Other Components
    AssetAssociationsList,
    Discussions,
    ExamTip,
    QuestionStem,
    Rationale,
  },
  mixins: [MathMlMixin, AssetTrayMixin],
  props: {
    answered: {
      type: Boolean,
      default: false,
    },
    answerIds: {
      type: Array,
    },
    blur: {
      type: Boolean,
      default: false,
    },
    // blur only the answer portion of the flashcard
    blurAnswers: {
      type: Boolean,
      default: true,
    },
    // blur the question text of the flashcard
    blurQuestion: {
      type: Boolean,
      default: true,
    },
    category: {
      type: Object,
      default: () => ({}),
    },
    // disable the ability to interact with the answer
    // currently only supported by multiple choice added for ASE
    disabled: {
      type: Boolean,
      default: false,
    },
    flashcard: {
      type: Object,
    },
    rawAnswerData: {
      type: Object,
    },
    sharingEnabled: {
      type: Boolean,
      default: false,
    },
    terminology: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['set-answer-ids', 'set-raw-answer-data', 'try-answer-flashcard'],
  setup(props) {
    const flashcard = toRef(props, 'flashcard');
    const { scenarioBoxTopic, hasScenarioBox } = useScenarioBox(flashcard);
    return { scenarioBoxTopic, hasScenarioBox };
  },
  computed: {
    flashcardType() {
      return flashcardType(this.flashcard);
    },
    flashcardState() {
      const flashcardStateKey = `${this.category.id}-category-${this.flashcard.id}`;
      return this.$store.getters['flashcardStates/flashcardStates'][flashcardStateKey];
    },
    shouldBlurAnswers() {
      return this.blur && this.blurAnswers;
    },
    shouldBlurQuestion() {
      return this.blur && this.blurQuestion;
    },
    shouldShowRating() {
      return RatingClass.enabled(this.$store, 'flashcard');
    },
    shouldShowDiscussion() {
      const isTerminology = this.flashcardType == 'terminology';
      return this.answered && !isTerminology && this.$store.getters['discussions/enabled'];
    },
    isOneColumnType() {
      const oneColumnTypes = ['cloze-dropdown', 'highlight'];
      return oneColumnTypes.includes(this.flashcardType);
    },
    isClozeDropdown() {
      return this.flashcardType === 'cloze-dropdown';
    },
    shouldShowStemContainer() {
      if (!this.isOneColumnType) {
        return true;
      }
      if (this.assetTrayGrouping || this.hasScenarioBox) {
        return true;
      }
      return false;
    },
    showExamTip() {
      const examTip = this.flashcard?.examTip;
      return this.answered && examTip && examTip != '';
    },
    showKeyTakeaway() {
      const keyTakeaway = this.flashcard?.keyTakeaway;
      return this.answered && keyTakeaway && keyTakeaway != '';
    },
    showAssetAssociation() {
      return this.answered;
    },
  },
  mounted() {
    this.$watch('flashcard', (newFlashcard, oldFlashcard = {}) => {
      if (newFlashcard && newFlashcard.id !== oldFlashcard.id) {
        if (newFlashcard.assetAssociations?.length > 0) {
          this.$store.dispatch('assetAssociations/assets', {
            assetAssociationIds: newFlashcard.assetAssociations.map(aa => aa.id),
            rateLimited: rateLimitingEnabled({
              apptimize: this.$apptimize,
              store: this.$store,
              analytics: this.$analytics,
            }),
          });
        }
        if (newFlashcard.attachmentIds?.length > 0) {
          this.$store.dispatch('attachments/fetch', { ids: newFlashcard.attachmentIds });
        }
      }
    }, { immediate: true });
  },
  methods: {
    handleFlashcardAssetAssociationClick(id) {
      this.$router.push({
        name: 'assetAssociation#show',
        params: {
          assetAssociationId: id,
        },
        query: {
          nav: this.$route.query.nav,
          ids: this.answerIds,
          data: this.rawAnswerData,
        },
      });
    },
  },
};
</script>

<style lang='scss' scoped>
  .flashcard-component-container {
    background-color: white;
    border: 1px solid #E3E8EF;
    border-radius: rem-calc(12);
    padding: rem-calc(12);
    margin: 1rem;
    max-width: calc(100% - 3rem);

    &.asset-tray-open {
      padding-bottom: rem-calc(331);
    }

    .question-stem-content {
      max-width: 60ch;
    }
  }

  .blur {
    filter: blur(5px);
    -webkit-user-select: none; /* Safari */
    -ms-user-select: none;     /* IE 10 and IE 11 */
    user-select: none;         /* Standard syntax */
  }

  .asset-tray.hide-asset-tray {
    display: none;
  }

  @include breakpoint(desktop) {
    .flashcard-component-container {
      background-color: transparent;
      border: none;
      display: grid;
      gap: rem-calc(24);
      grid-template-columns: repeat(12, 1fr);
      margin: rem-calc(32);
      height: calc(100vh - 18rem);
      padding: 0;

      .question-stem-container, .answer-component-container {
        background-color: white;
        border: 1px solid #D0D5DD;
        border-radius: rem-calc(12);
        display: grid;
        place-items: center;
        padding: rem-calc(32) rem-calc(48);
        overflow: auto;
      }

      .question-stem-container {
        grid-column: 1 / span 6;

        &.hide {
          display: none;

          &:has(.asset-tray) {
            display: grid;
          }

          &:has(.scenario-box-container) {
            display: grid;
          }
        }
      }

      .answer-component-container {
        grid-column: 7 / span 6;

        &.only-one-column {
          grid-column: 1 / span 12;

          > * {
            max-width: 70ch;
          }
        }
      }

      .one-column {
        display: none;
      }

      .asset-tray.hide-asset-tray {
        display: block;
      }

      .hide {
        display: none;
      }
    }
  }
</style>
